import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Settings, Zap, Award, Grid3x3, SlidersHorizontal, History } from "lucide-react";
import SectionCard from "../components/agentProgram/SectionCard";
import StatusPill from "../components/agentProgram/StatusPill";
import EmptyState from "../components/agentProgram/EmptyState";

const API = process.env.REACT_APP_API_URL;

const dateText = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

export default function AgentProgramConfig() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const { getRoleData } = useSelector((state) => state.getRoleInfo || {});
  const role = getRoleData || userInfo?.role || {};

  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const fetchAll = async () => {
    setError("");
    try {
      const [{ data: active }, { data: versions }] = await Promise.all([
        axios.get(`${API}/agent-program/config`, authConfig),
        axios.get(`${API}/agent-program/config/history`, authConfig),
      ]);
      setConfig(active);
      setHistory(versions || []);
      setDraft(JSON.parse(JSON.stringify(active)));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load config");
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNewVersion = async () => {
    setMessage("");
    setError("");
    try {
      const payload = {
        pointsPerStage: draft.pointsPerStage,
        levels: draft.levels,
        applicationRewardGrid: draft.applicationRewardGrid,
        disbursalTranche1Pct: Number(draft.disbursalTranche1Pct),
        disbursalTranche1DelayDays: Number(draft.disbursalTranche1DelayDays),
        disbursalTranche2Pct: Number(draft.disbursalTranche2Pct),
        disbursalTranche2DelayDays: Number(draft.disbursalTranche2DelayDays),
        heldPercentage: Number(draft.heldPercentage),
        holdDurationMonths: Number(draft.holdDurationMonths),
        payoutTrigger: draft.payoutTrigger,
        applicationRewardDelayDays: Number(draft.applicationRewardDelayDays),
        tdsPercentage: Number(draft.tdsPercentage),
        notes: draft.notes || "Updated via Agent Program config screen.",
      };
      await axios.post(`${API}/agent-program/config`, payload, authConfig);
      setMessage("New config version created and activated.");
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to save config");
    }
  };

  if (!draft) {
    return (
      <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        ) : (
          <EmptyState icon={Settings} title="Loading config…" />
        )}
      </div>
    );
  }

  const canEdit = !!role?.agentProgramConfigEdit;

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
          <Settings size={21} className="text-indigo-600" />
        </div>
        <div className="min-w-0 flex items-center gap-2.5 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent Program Config</h1>
            <p className="text-sm text-gray-500 mt-0.5">Changes create a new version — never edited in place, past payouts are never recalculated.</p>
          </div>
          <StatusPill label={`Active v${config?.version ?? "-"}`} tone="success" />
        </div>
      </div>

      {message && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 text-sm mb-4">{message}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}

      <div className="mb-5">
        <SectionCard icon={Zap} title="Points per Stage" subtitle="Awarded to the agent as a lead moves forward">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {["leadSubmitted", "applicationAccepted", "disbursed"].map((key) => (
              <label key={key} className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1 capitalize">{key.replace(/([A-Z])/g, " $1")}</span>
                <div className="relative">
                  <input
                    type="number"
                    disabled={!canEdit}
                    className="border border-gray-200 rounded-lg text-sm pl-3 pr-12 py-2 w-full disabled:bg-gray-50"
                    value={draft.pointsPerStage[key]}
                    onChange={(e) => setDraft({ ...draft, pointsPerStage: { ...draft.pointsPerStage, [key]: Number(e.target.value) } })}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">pts</span>
                </div>
              </label>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="mb-5">
        <SectionCard icon={Award} title="Levels" subtitle="Thresholds, commission share, and one-time voucher per level" bodyClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {["Level", "Min Points", "Commission Share", "Voucher Amount"].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {draft.levels.map((lvl, idx) => (
                  <tr key={lvl.level}>
                    <td className="px-5 py-2.5 font-medium text-gray-800">Level {lvl.level}</td>
                    {["minPoints", "commissionSharePct", "voucherAmount"].map((field) => (
                      <td key={field} className="px-5 py-2.5">
                        <div className="relative w-32">
                          <input
                            type="number"
                            disabled={!canEdit}
                            className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-9 py-1.5 w-full disabled:bg-gray-50"
                            value={lvl[field]}
                            onChange={(e) => {
                              const levels = [...draft.levels];
                              levels[idx] = { ...levels[idx], [field]: Number(e.target.value) };
                              setDraft({ ...draft, levels });
                            }}
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            {field === "commissionSharePct" ? "%" : field === "voucherAmount" ? "₹" : "pts"}
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="mb-5">
        <SectionCard icon={Grid3x3} title="Application Reward Grid" subtitle="Fixed reward at acceptance, by loan slab and level" bodyClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap">Slab</th>
                  {draft.levels.map((lvl) => (
                    <th key={lvl.level} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap">Level {lvl.level}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {draft.applicationRewardGrid.map((slab, slabIdx) => (
                  <tr key={slab.slabLabel}>
                    <td className="px-5 py-2.5 text-gray-700 whitespace-nowrap">{slab.slabLabel}</td>
                    {slab.amountsByLevel.map((amount, levelIdx) => (
                      <td key={levelIdx} className="px-5 py-2.5">
                        <div className="relative w-28">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                          <input
                            type="number"
                            disabled={!canEdit}
                            className="border border-gray-200 rounded-lg text-sm pl-6 pr-2 py-1.5 w-full disabled:bg-gray-50"
                            value={amount}
                            onChange={(e) => {
                              const grid = [...draft.applicationRewardGrid];
                              const amountsByLevel = [...grid[slabIdx].amountsByLevel];
                              amountsByLevel[levelIdx] = Number(e.target.value);
                              grid[slabIdx] = { ...grid[slabIdx], amountsByLevel };
                              setDraft({ ...draft, applicationRewardGrid: grid });
                            }}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="mb-5">
        <SectionCard icon={SlidersHorizontal} title="Disbursal Payout Schedule" subtitle="How the commission share pays out over time, timed from the disbursal date">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-700 mb-3">Tranche 1</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[11px] text-gray-500 mb-1">Percent</span>
                  <div className="relative">
                    <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-6 py-2 w-full disabled:bg-gray-50" value={draft.disbursalTranche1Pct ?? 0} onChange={(e) => setDraft({ ...draft, disbursalTranche1Pct: e.target.value })} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                  </div>
                </label>
                <label className="block">
                  <span className="block text-[11px] text-gray-500 mb-1">After</span>
                  <div className="relative">
                    <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-9 py-2 w-full disabled:bg-gray-50" value={draft.disbursalTranche1DelayDays ?? 0} onChange={(e) => setDraft({ ...draft, disbursalTranche1DelayDays: e.target.value })} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">days</span>
                  </div>
                </label>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-700 mb-3">Tranche 2</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[11px] text-gray-500 mb-1">Percent</span>
                  <div className="relative">
                    <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-6 py-2 w-full disabled:bg-gray-50" value={draft.disbursalTranche2Pct ?? 0} onChange={(e) => setDraft({ ...draft, disbursalTranche2Pct: e.target.value })} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                  </div>
                </label>
                <label className="block">
                  <span className="block text-[11px] text-gray-500 mb-1">After</span>
                  <div className="relative">
                    <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-9 py-2 w-full disabled:bg-gray-50" value={draft.disbursalTranche2DelayDays ?? 0} onChange={(e) => setDraft({ ...draft, disbursalTranche2DelayDays: e.target.value })} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">days</span>
                  </div>
                </label>
              </div>
            </div>
            <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-800 mb-3">Held (clawback-protected)</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[11px] text-gray-500 mb-1">Percent</span>
                  <div className="relative">
                    <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-6 py-2 w-full disabled:bg-gray-50" value={draft.heldPercentage} onChange={(e) => setDraft({ ...draft, heldPercentage: e.target.value })} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                  </div>
                </label>
                <label className="block">
                  <span className="block text-[11px] text-gray-500 mb-1">After</span>
                  <div className="relative">
                    <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm pl-2.5 pr-10 py-2 w-full disabled:bg-gray-50" value={draft.holdDurationMonths} onChange={(e) => setDraft({ ...draft, holdDurationMonths: e.target.value })} />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">mo.</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
          {(() => {
            const total = Number(draft.disbursalTranche1Pct || 0) + Number(draft.disbursalTranche2Pct || 0) + Number(draft.heldPercentage || 0);
            const ok = Math.round(total * 100) / 100 === 100;
            return (
              <p className={`text-xs mt-3 ${ok ? "text-emerald-600" : "text-red-600"}`}>
                {ok ? "✓" : "⚠"} Tranches add up to {total}% {ok ? "" : "— must total exactly 100%"}
              </p>
            );
          })()}
        </SectionCard>
      </div>

      <div className="mb-5">
        <SectionCard icon={SlidersHorizontal} title="Other Payout Rules" subtitle="Trigger, application reward delay, and TDS">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Payout Trigger</span>
              <select disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full disabled:bg-gray-50" value={draft.payoutTrigger} onChange={(e) => setDraft({ ...draft, payoutTrigger: e.target.value })}>
                <option value="disbursal">Disbursal</option>
                <option value="commission_received">Commission Received</option>
              </select>
              <p className="text-[11px] text-gray-400 mt-1">Which action creates the disbursal share transactions — the schedule above always counts from the disbursal date regardless.</p>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Application Reward Delay (days)</span>
              <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full disabled:bg-gray-50" value={draft.applicationRewardDelayDays} onChange={(e) => setDraft({ ...draft, applicationRewardDelayDays: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">TDS %</span>
              <input type="number" disabled={!canEdit} className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full disabled:bg-gray-50" value={draft.tdsPercentage} onChange={(e) => setDraft({ ...draft, tdsPercentage: e.target.value })} />
            </label>
          </div>

          {canEdit && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <label className="block mb-3">
                <span className="block text-xs font-medium text-gray-600 mb-1">Notes (why this change)</span>
                <input
                  className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full max-w-lg"
                  value={draft.notes || ""}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                />
              </label>
              <button onClick={saveNewVersion} className="bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm hover:shadow transition-all">
                Save as New Version
              </button>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard icon={History} title="Version History" bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {["Version", "Active", "Effective From", "Notes"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <EmptyState icon={History} title="No version history yet" compact />
                  </td>
                </tr>
              ) : (
                history.map((v) => (
                  <tr key={v.version} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">v{v.version}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {v.isActive ? <StatusPill label="Active" tone="success" /> : <StatusPill label="Inactive" tone="neutral" />}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{dateText(v.effectiveFrom)}</td>
                    <td className="px-4 py-3 text-gray-500">{v.notes}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
